import { useState, useRef, useCallback } from "react";
import * as tus from "tus-js-client";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, UploadCloud, Video, CheckCircle2, X } from "lucide-react";

/**
 * Direct-to-Cloudflare Stream video uploader.
 * Uses tus resumable protocol so 500MB+ files never touch our backend.
 *
 * Props:
 *   value    → current stream UID (may be null)
 *   onChange → called with { uid, iframe_url } once upload + processing completes
 *   testid   → for E2E
 */
export default function CloudflareVideoUpload({ value, onChange, testid = "cf-stream-upload" }) {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(value ? "ready" : "idle"); // idle | preparing | uploading | processing | ready | error
  const [error, setError] = useState(null);
  const [uid, setUid] = useState(value || null);
  const [iframeUrl, setIframeUrl] = useState(null);
  const uploadRef = useRef(null);
  const pollRef = useRef(null);

  const reset = () => {
    if (uploadRef.current) { try { uploadRef.current.abort(); } catch {} uploadRef.current = null; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setFile(null); setProgress(0); setError(null);
  };

  const pollUntilReady = useCallback((uidVal) => {
    setStatus("processing");
    const started = Date.now();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/stream/${uidVal}/status`);
        if (data.ready) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStatus("ready");
          setIframeUrl(data.iframe_url);
          onChange?.({ uid: uidVal, iframe_url: data.iframe_url });
          toast.success("الفيديو جاهز للتشغيل");
        } else if (Date.now() - started > 15 * 60 * 1000) {
          // 15 min max wait — Cloudflare processing rarely exceeds this
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStatus("error");
          setError("انتهت مهلة معالجة الفيديو");
        }
      } catch (e) {
        // transient — keep polling
      }
    }, 5000);
  }, [onChange]);

  const startUpload = async (f) => {
    reset();
    setFile(f);
    setStatus("preparing");
    try {
      const { data } = await api.post("/admin/stream/upload-session", {
        file_name: f.name,
        file_size_bytes: f.size,
        require_signed_urls: true,
      });
      const upload = new tus.Upload(f, {
        endpoint: data.upload_url,
        uploadUrl: data.upload_url,
        chunkSize: 50 * 1024 * 1024, // 50MB chunks — Cloudflare requirement
        retryDelays: [0, 3000, 5000, 10000, 20000],
        metadata: { filename: f.name, filetype: f.type },
        onError: (err) => {
          setStatus("error");
          setError(err.message || String(err));
          toast.error("فشل رفع الفيديو");
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          setProgress(Math.round((bytesUploaded / bytesTotal) * 100));
        },
        onSuccess: () => {
          setUid(data.uid);
          onChange?.({ uid: data.uid, iframe_url: null });
          pollUntilReady(data.uid);
        },
      });
      uploadRef.current = upload;
      setStatus("uploading");
      upload.start();
    } catch (e) {
      setStatus("error");
      setError(e?.response?.data?.detail || e.message);
      toast.error("تعذر بدء الرفع");
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) {
      toast.error("يرجى اختيار ملف فيديو");
      return;
    }
    startUpload(f);
  };

  const clearVideo = () => {
    reset();
    setUid(null);
    setIframeUrl(null);
    setStatus("idle");
    onChange?.({ uid: null, iframe_url: null });
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      {status === "idle" && (
        <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-6 cursor-pointer hover:border-primary transition text-center">
          <UploadCloud className="h-8 w-8 text-primary" />
          <div className="text-sm font-semibold">ارفع فيديو الدرس</div>
          <div className="text-[11px] text-muted-foreground">MP4 / WebM / MOV — حتى 5GB · يُرفع مباشرة إلى Cloudflare Stream</div>
          <input type="file" accept="video/*" className="hidden" onChange={onPick} data-testid={`${testid}-input`} />
        </label>
      )}

      {(status === "preparing" || status === "uploading") && (
        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="flex-1 truncate">{file?.name}</span>
            <span className="text-xs text-muted-foreground">{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="text-[11px] text-muted-foreground">
            {status === "preparing" ? "جاري التحضير…" : "جاري رفع الفيديو مباشرة إلى Cloudflare — لا تغلق الصفحة"}
          </div>
        </div>
      )}

      {status === "processing" && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 text-blue-900 p-4 flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>الفيديو رُفع. Cloudflare يعالجه الآن (قد يستغرق دقيقة أو دقيقتين حسب الحجم)…</span>
        </div>
      )}

      {status === "ready" && uid && (
        <div className="rounded-lg border border-green-200 bg-green-50 text-green-900 p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <CheckCircle2 className="h-4 w-4" />
            <Video className="h-4 w-4" />
            <span className="flex-1">الفيديو جاهز</span>
            <Button size="sm" variant="ghost" onClick={clearVideo} data-testid={`${testid}-clear`}>
              <X className="h-3 w-3" />
            </Button>
          </div>
          <div className="text-[10px] font-mono text-green-800/70 break-all">UID: {uid}</div>
          {iframeUrl && (
            <div className="aspect-video rounded overflow-hidden bg-black">
              <iframe src={iframeUrl} className="w-full h-full" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowFullScreen title="preview" />
            </div>
          )}
        </div>
      )}

      {status === "error" && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 p-3 text-sm space-y-2">
          <div className="font-semibold">فشل رفع الفيديو</div>
          <div className="text-[11px] break-words">{error}</div>
          <Button size="sm" variant="outline" onClick={() => { setStatus("idle"); reset(); }}>حاول مرة أخرى</Button>
        </div>
      )}
    </div>
  );
}
